/**
 * ShipDock Component
 *
 * Displays ships available for placement during the Placement Phase.
 * Supports drag-and-drop, rotation, auto-place, and ready confirmation.
 *
 * Requirements: 2.1, 2.6, 2.7, 2.8, 3.1, 3.5
 */

import React, { useCallback } from 'react';
import { ShipDockProps, ShipDockItem, ShipType, SHIP_SIZES } from '../types';
import './ShipDock.css';

/** Display names for ship types */
const SHIP_DISPLAY_NAMES: Record<ShipType, string> = {
  carrier: 'Carrier',
  battleship: 'Battleship',
  cruiser: 'Cruiser',
  submarine: 'Submarine',
  destroyer: 'Destroyer',
};

export const ShipDock: React.FC<ShipDockProps> = ({
  ships,
  onShipDragStart,
  onRotate,
  onAutoPlace,
  onReady,
  allShipsPlaced,
  isReady,
}) => {
  const handleDragStart = useCallback(
    (e: React.DragEvent, ship: ShipDockItem) => {
      if (ship.placed || isReady) return;

      e.dataTransfer.setData(
        'text/plain',
        JSON.stringify({
          shipType: ship.type,
          size: ship.size,
          orientation: ship.orientation,
        })
      );
      e.dataTransfer.effectAllowed = 'move';
      onShipDragStart(ship.type);
    },
    [onShipDragStart, isReady]
  );

  const handleRotateClick = useCallback(
    (e: React.MouseEvent, shipType: ShipType) => {
      e.stopPropagation();
      if (isReady) return;
      onRotate(shipType);
    },
    [onRotate, isReady]
  );

  return (
    <div className="bs-ship-dock" role="region" aria-label="Ship Placement Dock">
      <div className="bs-ship-dock__title">Place Your Ships</div>

      <div className="bs-ship-dock__list">
        {ships.map((ship) => (
          <div
            key={ship.type}
            className={`bs-ship-dock__item ${
              ship.placed ? 'bs-ship-dock__item--placed' : ''
            } ${isReady ? 'bs-ship-dock__item--disabled' : ''}`}
            draggable={!ship.placed && !isReady}
            onDragStart={(e) => handleDragStart(e, ship)}
            role="listitem"
            aria-label={`${SHIP_DISPLAY_NAMES[ship.type]} (${ship.size} cells) - ${
              ship.placed ? 'placed' : 'not placed'
            } - ${ship.orientation}`}
          >
            <span className="bs-ship-dock__ship-name">
              {SHIP_DISPLAY_NAMES[ship.type]}
            </span>

            <div className="bs-ship-dock__ship-cells">
              {Array.from({ length: ship.size }, (_, i) => (
                <div key={i} className="bs-ship-dock__ship-cell" />
              ))}
            </div>

            {!ship.placed && !isReady && (
              <button
                className="bs-ship-dock__rotate-btn"
                onClick={(e) => handleRotateClick(e, ship.type)}
                aria-label={`Rotate ${SHIP_DISPLAY_NAMES[ship.type]} to ${
                  ship.orientation === 'horizontal' ? 'vertical' : 'horizontal'
                }`}
                title="Rotate"
              >
                ↻
              </button>
            )}

            {ship.placed && <span style={{ color: '#fff', fontSize: '0.75rem' }}>✓</span>}
          </div>
        ))}
      </div>

      <div className="bs-ship-dock__actions">
        <button
          className="bs-ship-dock__btn bs-ship-dock__btn--auto"
          onClick={onAutoPlace}
          disabled={isReady}
          aria-label="Auto-place all ships randomly"
        >
          🎲 Auto-Place
        </button>

        <button
          className="bs-ship-dock__btn bs-ship-dock__btn--ready"
          onClick={onReady}
          disabled={!allShipsPlaced || isReady}
          aria-label="Confirm ship placement and start game"
        >
          {isReady ? '✓ Ready!' : '⚓ Ready'}
        </button>
      </div>
    </div>
  );
};

export default ShipDock;
